import * as types from '../actions/actionTypes';
import { IMaintenanceTrigger } from '../models';
import { IinitialState } from '.';

export type MaintenanceTriggerState = {
    saved: { [installBaseID: string]: IMaintenanceTrigger[] };
    pending: { [installBaseID: string]: { [maintenanceType: number]: IMaintenanceTrigger } };
};

const initialMaintenanceTriggerState: MaintenanceTriggerState = {
    saved: {},
    pending: {}
};

/**
 * Returns the effective triggers for an install base: pending edits take precedence
 * over the last-known saved triggers from the server.
 */
export const getMaintenanceTriggersByInstallBase = (
    state: IinitialState,
    installBaseID: string
): IMaintenanceTrigger[] => {
    const saved = state.maintenanceTriggers.saved[installBaseID] || [];
    const pendingByType = state.maintenanceTriggers.pending[installBaseID] || {};

    const merged: IMaintenanceTrigger[] = saved.map(t => {
        const override = pendingByType[t.maintenanceType];
        return override ? override : t;
    });

    // include any pending triggers that don't yet have a saved counterpart
    const savedTypes = new Set(saved.map(t => t.maintenanceType));
    Object.values(pendingByType).forEach((p: IMaintenanceTrigger | undefined) => {
        if (p && !savedTypes.has(p.maintenanceType)) {
            merged.push(p);
        }
    });

    return merged;
};

export const getPendingMaintenanceTriggersByInstallBase = (
    state: IinitialState,
    installBaseID: string
): IMaintenanceTrigger[] => {
    const pendingByType = state.maintenanceTriggers.pending[installBaseID] || {};
    return Object.values(pendingByType).filter(
        (t): t is IMaintenanceTrigger => t !== undefined
    );
};

/** In-session staged edit for one (installBase, maintenanceType), if any. */
export const getPendingMaintenanceTrigger = (
    state: IinitialState,
    installBaseID: string,
    maintenanceType: number
): IMaintenanceTrigger | undefined => state.maintenanceTriggers.pending[installBaseID]?.[maintenanceType];

/** Last-known server trigger for one (installBase, maintenanceType) - pre-synced at job open. */
export const getSavedMaintenanceTrigger = (
    state: IinitialState,
    installBaseID: string,
    maintenanceType: number
): IMaintenanceTrigger | undefined =>
    (state.maintenanceTriggers.saved[installBaseID] || []).find(t => t.maintenanceType === maintenanceType);

function maintenanceTriggerReducer(
    state: MaintenanceTriggerState = initialMaintenanceTriggerState,
    action: any
): MaintenanceTriggerState {
    switch (action.type) {
        case types.GET_MAINTENANCE_TRIGGERS_BY_FACILITY_SUCCESS: {
            // Job-open sync: a flat list of triggers across the facility's install bases; group by install base.
            const triggers: IMaintenanceTrigger[] = action.triggers || [];
            const byInstallBase: { [installBaseID: string]: IMaintenanceTrigger[] } = {};
            triggers.forEach(t => {
                const list = byInstallBase[t.installBaseID] ?? [];
                list.push(t);
                byInstallBase[t.installBaseID] = list;
            });
            return {
                ...state,
                saved: {
                    ...state.saved,
                    ...byInstallBase
                }
            };
        }
        case types.STAGE_MAINTENANCE_TRIGGER: {
            const trigger: IMaintenanceTrigger = action.trigger;
            return {
                ...state,
                pending: {
                    ...state.pending,
                    [trigger.installBaseID]: {
                        ...(state.pending[trigger.installBaseID] || {}),
                        [trigger.maintenanceType]: trigger
                    }
                }
            };
        }
        case types.COMMIT_PENDING_MAINTENANCE_TRIGGERS: {
            const installBaseID: string = action.installBaseID;
            const pendingByType = state.pending[installBaseID] || {};
            const pendingList = Object.values(pendingByType).filter(
                (t): t is IMaintenanceTrigger => t !== undefined
            );

            if (!pendingList.length) {
                return state;
            }

            const existingSaved = state.saved[installBaseID] || [];
            const savedByType = new Map<number, IMaintenanceTrigger>(
                existingSaved.map(t => [t.maintenanceType, t])
            );
            pendingList.forEach(p => {
                savedByType.set(p.maintenanceType, p);
            });

            return {
                ...state,
                saved: {
                    ...state.saved,
                    [installBaseID]: Array.from(savedByType.values())
                },
                pending: {
                    ...state.pending,
                    [installBaseID]: {}
                }
            };
        }
        default:
            return state;
    }
}

export default maintenanceTriggerReducer;
